import React, { useState, useEffect, useRef  } from 'react';
import { useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import TimesheetAnnotator from './TimesheetAnnotator';

function QuickTime() {
  const [timesheet, setTimesheet] = useState(null);
  const [timesheetItems, setTimesheetItems] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [submitError, setSubmitError] = useState(null);
  const [highlightValidationErrors, setHighlightValidationErrors] = useState(false);
  const [annotationImageUrl, setAnnotationImageUrl] = useState(null);
  const [showAnnotator, setShowAnnotator] = useState(false);
  const pendingImageRef = useRef(null);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const guestCode = queryParams.get('guestCode');
  const appendIndex = queryParams.get('index') == null ? '' : `?index=${queryParams.get('index')}`;

  const isEditable = timesheet?.status === 'Draft' || timesheet?.status === 'Pending' || !timesheet?.status;

  useEffect(() => {
    if (guestCode) {
      verifyGuestCode();
    } else {
      setError('Guest code is required');
      setLoading(false);
    }
  }, [guestCode]);

  useEffect(() => {
    if (timesheet?.id) {
      fetchTimesheetItems();
      fetchAttachments();
    }
  }, [timesheet?.id]);

  const verifyGuestCode = async () => {
    try {
      //console.log("API:", API_BASE_URL);
      const response = await fetch(`${API_BASE_URL}/guest/${guestCode}/verify${appendIndex}`);
      if (response.status === 404) {
        setError('No Timesheets available for the guest code');
        setLoading(false);
        return;
      }
      if (!response.ok) {
        throw new Error('Invalid guest code');
      }
      const data = await response.json();
      setTimesheet(data);
    } catch (err) {
      setError('Failed to verify guest code: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimesheetItems = async () => {
    if (!timesheet?.id) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/guest/${guestCode}/ts/${timesheet.id}/items`);
      if (!response.ok) {
        throw new Error('Failed to fetch timesheet items');
      }
      const data = await response.json();
      setTimesheetItems(data);
    } catch (err) {
      setError('Failed to fetch timesheet items: ' + err.message);
      setTimesheetItems([]);
    }
    setLoading(false);
  };

  const fetchAttachments = async () => {
    if (!timesheet?.id) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/guest/${guestCode}/ts/${timesheet.id}/attachments`);
      if (!response.ok) {
        throw new Error('Failed to fetch attachments');
      }
      const data = await response.json();
      setAttachments(data);
    } catch (err) {
      setError('Failed to fetch attachments');
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setSubmitError(null);
      setHighlightValidationErrors(false);

      // Validation: Check non-holiday rows have hours worked and comment
      const missingHours = timesheetItems.filter(item => {
        if (item.type === 'HOLIDAY' || item.type === 'VACATION') return false;
        return !item.hoursWorked || parseFloat(item.hoursWorked) <= 0;
      });

      if (missingHours.length > 0) {
        setHighlightValidationErrors(true);
        throw new Error('All working days must have Hours Worked greater than 0');
      }

      const missingComments = timesheetItems.filter(item => {
        if (item.type === 'HOLIDAY' || item.type === 'VACATION') return false;
        return !item.comment || item.comment.trim() === '';
      });

      if (missingComments.length > 0) {
        setHighlightValidationErrors(true);
        throw new Error('All working days must list the Activities performed on that day');
      }

      // Validation: Check if timesheet requires at least one attachment
      const hasBillableItems = timesheetItems.some(item => item.type === 'BILLABLE');
      const requiresAttachment = hasBillableItems || timesheet?.attachmentRequired;
      if (requiresAttachment && attachments.length === 0) {
        throw new Error('At least one attachment is required for this timesheet');
      }

      // Ensure each item has a type
      const itemsToSubmit = timesheetItems.map(item => ({
        ...item,
        type: item.type || 'BILLABLE'
      }));

      const response = await fetch(`${API_BASE_URL}/guest/${guestCode}/ts/${timesheet.id}/items/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(itemsToSubmit)
      });

      if (!response.ok) {
        throw new Error('Failed to submit timesheet');
      }

      // Reload the page on success
      window.location.reload();
    } catch (err) {
      setSubmitError('Failed to submit timesheet: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...timesheetItems];
    // If selecting type and type is HOLIDAY or VACATION, clear relevant fields
    if (field === 'type' && (value === 'HOLIDAY' || value === 'VACATION')) {
      updatedItems[index] = {
        ...updatedItems[index],
        type: value,
        hoursWorked: '',
        extraHours: '',
        totalHours: '',
        comment: ''
      };
    } else {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value
      };
    }

    // Calculate total hours when hours worked or extra hours change
    if (field === 'hoursWorked' || field === 'extraHours') {
      const hoursWorked = parseFloat(updatedItems[index].hoursWorked) || 0;
      const extraHours = parseFloat(updatedItems[index].extraHours) || 0;
      updatedItems[index].totalHours = (hoursWorked + extraHours).toFixed(2);
    }

    setTimesheetItems(updatedItems);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !timesheet?.id) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('timesheetId', timesheet.id);

    try {
      const response = await fetch(`${API_BASE_URL}/guest/${guestCode}/ts/${timesheet.id}/attachments`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload file');
      }
      
      await fetchAttachments();
      setMessage('✅ File uploaded successfully');
    } catch (error) {
      setMessage('❌ Failed to upload file');
    }
  };

  const handleDownload = async (attachmentId, filename) => {
    try {
      const response = await fetch(`${API_BASE_URL}/guest/${guestCode}/ts/${timesheet.id}/attachments/${attachmentId}`);
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      // Open in a new tab/window so the browser handles the file (PDF/image/etc.)
      window.open(url, '_blank');
      // Cleanup after the new window has a chance to read the URL
      setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
    } catch (err) {
      setMessage('❌ Failed to download file');
    }
  };

  const handleDelete = async (attachmentId) => {
    if (!timesheet?.id || !isEditable) return;

    try {
      const response = await fetch(`${API_BASE_URL}/guest/${guestCode}/ts/${timesheet.id}/attachments/${attachmentId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete file');
      }
      
      await fetchAttachments();
    } catch (err) {
      setMessage('❌ Failed to delete file');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (!timesheet?.id) return;

    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(file => {
      const fileType = file.type.toLowerCase();
      return fileType === 'application/pdf' || 
             fileType === 'image/jpeg' || 
             fileType === 'image/jpg' || 
             fileType === 'image/png';
    });

    if (validFiles.length > 0) {
      setPreviewFiles(validFiles);
      handleUpload(validFiles);
    }
  };

  const handleFileSelect = (e) => {
    if (!timesheet?.id) return;
  
    const files = Array.from(e.target.files);
    if (!files.length) return;
  
    const imageFile = files.find(f => f.type.startsWith("image/"));
    const pdfFiles = files.filter(f => f.type === "application/pdf");
  
    // PDFs → upload immediately (unchanged behavior)
    if (pdfFiles.length) {
      setPreviewFiles(pdfFiles);
      handleUpload(pdfFiles);
    }
  
    // Images → DO NOT upload yet
    if (imageFile) {
      pendingImageRef.current = imageFile;
  
      const localUrl = URL.createObjectURL(imageFile);
      setAnnotationImageUrl(localUrl);
      setShowAnnotator(true);
    }
  };
  
  

  const handleUpload = async (files) => {
    if (!timesheet?.id) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('timesheetId', timesheet.id);

        const response = await fetch(`${API_BASE_URL}/guest/${guestCode}/ts/${timesheet.id}/attachments`, {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          throw new Error('Failed to upload file');
        }
      }
      await fetchAttachments();
      setPreviewFiles([]);
      setMessage('✅ Files uploaded successfully');
    } catch (err) {
      setMessage('❌ Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div>Loading Next Available Timesheet ...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="p-4">
      {/* Timesheet Information */}
      {timesheet && (
        <div className="mb-6 p-4 border rounded-lg bg-gray-50">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p><span className="font-medium">Timesheet ID:</span> {timesheet.id}
                {/* Navigation buttons for timesheet - moved from Vendor, and styled */}
                {typeof timesheet.totalResults === 'number' && timesheet.totalResults > 1 && (
                  <span style={{ marginLeft: 12, verticalAlign: 'middle' }}>
                    {(() => {
                      const currIndex = parseInt(queryParams.get('index')) || 0;
                      const buttonBase = "px-4 py-1.5 rounded-lg text-white transition-colors duration-200 font-medium";
                      const enabled = "bg-green-600 hover:bg-green-700";
                      const disabled = "bg-gray-400 cursor-not-allowed opacity-70";
                      return (
                        <>
                          <button
                            className={`${buttonBase} ${currIndex <= 0 ? disabled : enabled}`}
                            disabled={currIndex <= 0}
                            style={{ marginRight: 6 }}
                            onClick={() => {
                              if (currIndex > 0) {
                                const params = new URLSearchParams(location.search);
                                params.set('index', currIndex - 1);
                                window.location.search = params.toString();
                              }
                            }}
                          >Prev</button>
                          <span style={{ fontWeight: 500, minWidth: 60, margin: '0 8px', display: 'inline-block', color:'#059669' }}>{`Timesheet ${currIndex + 1} of ${timesheet.totalResults}`}</span>
                          <button
                            className={`${buttonBase} ${currIndex >= timesheet.totalResults - 1 ? disabled : enabled}`}
                            disabled={currIndex >= timesheet.totalResults - 1}
                            style={{ marginLeft: 6 }}
                            onClick={() => {
                              if (currIndex < timesheet.totalResults - 1) {
                                const params = new URLSearchParams(location.search);
                                params.set('index', currIndex + 1);
                                window.location.search = params.toString();
                              }
                            }}
                          >Next</button>
                        </>
                      );
                    })()}
                  </span>
                )}
              </p>
              <p><span className="font-medium">Employer:</span> {timesheet.corpName}</p>
              <p><span className="font-medium">Employee:</span> {timesheet.employeeName}</p>
              <p><span className="font-medium">Vendor:</span> {timesheet.vendorName}</p>
            </div>
            <div>
              <p><span className="font-medium">Period:</span> {new Date(timesheet.periodStartDate + 'T00:00:00').toLocaleDateString()} - {new Date(timesheet.periodEndDate + 'T00:00:00').toLocaleDateString()}</p>
              <p><span className="font-medium">Status:</span> {timesheet.status || 'Pending'}</p>
              <p><span className="font-medium">Total Hours:</span> {timesheet.totalHours}</p>
            </div>
          </div>
        </div>
      )}

      {/* Timesheet Items Table */}
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hours Worked</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Extra Hours</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Hours</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activities</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {Array.isArray(timesheetItems) && timesheetItems.length > 0 ? (
            <>
              {timesheetItems.map((item, index) => (
                  <tr
                    key={item.id || index}
                    style={{
                      backgroundColor:
                        (item.type === 'HOLIDAY' || item.type === 'VACATION')
                          ? '#E5E7EB'
                          : (highlightValidationErrors &&
                             item.type !== 'HOLIDAY' &&
                             item.type !== 'VACATION' &&
                             (
                               !item.hoursWorked ||
                               parseFloat(item.hoursWorked) <= 0 ||
                               !item.comment ||
                               item.comment.trim() === ''
                             )
                            )
                            ? 'red'
                            : 'white'
                    }}
                  >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="date"
                      value={item.calendarDate ? new Date(item.calendarDate).toISOString().split('T')[0] : ''}
                      readOnly
                      style={{ backgroundColor: (item.type === 'HOLIDAY' || item.type === 'VACATION') ? '#E5E7EB' : 'white' }}
                      className="border rounded px-2 py-1"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="text"
                      value={item.dayOfWeek || ''}
                      readOnly
                      style={{ backgroundColor: (item.type === 'HOLIDAY' || item.type === 'VACATION') ? '#E5E7EB' : 'white' }}
                      className="border rounded px-2 py-1"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={item.type || 'BILLABLE'}
                      onChange={(e) => handleItemChange(index, 'type', e.target.value)}
                      disabled={!isEditable}
                      style={{ backgroundColor: (item.type === 'HOLIDAY' || item.type === 'VACATION') ? '#E5E7EB' : 'white' }}
                      className="border rounded px-2 py-1"
                    >
                      <option value="BILLABLE">Billable</option>
                      <option value="HOLIDAY">Holiday</option>
                      <option value="VACATION">Vacation</option>
                      <option value="NON_BILLABLE">Non-Billable</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="number"
                      step="0.01"
                      value={item.hoursWorked || ''}
                      onChange={(e) => handleItemChange(index, 'hoursWorked', e.target.value)}
                      disabled={!isEditable || item.type === 'HOLIDAY' || item.type === 'VACATION'}
                      style={{ backgroundColor: (item.type === 'HOLIDAY' || item.type === 'VACATION') ? '#E5E7EB' : 'white' }}
                      className="border rounded px-2 py-1"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="number"
                      step="0.01"
                      value={item.extraHours || ''}
                      onChange={(e) => handleItemChange(index, 'extraHours', e.target.value)}
                      disabled={!isEditable || item.type === 'HOLIDAY' || item.type === 'VACATION'}
                      style={{ backgroundColor: (item.type === 'HOLIDAY' || item.type === 'VACATION') ? '#E5E7EB' : 'white' }}
                      className="border rounded px-2 py-1"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="number"
                      value={item.totalHours || '0.00'}
                      readOnly
                      style={{ backgroundColor: (item.type === 'HOLIDAY' || item.type === 'VACATION') ? '#E5E7EB' : 'white' }}
                      className="border rounded px-2 py-1"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="text"
                      value={item.comment || ''}
                      onChange={(e) => handleItemChange(index, 'comment', e.target.value)}
                      disabled={!isEditable || item.type === 'HOLIDAY' || item.type === 'VACATION'}
                      style={{ minWidth: '500px', width: '100%', backgroundColor: (item.type === 'HOLIDAY' || item.type === 'VACATION') ? '#E5E7EB' : 'white' }}
                      className="border rounded px-2 py-1"
                    />
                  </td>
                </tr>
              ))}
              {/* Summary Row */}
              <tr className="bg-gray-50 font-semibold">
                <td colSpan="4"></td>
                <td style={{ textAlign: 'right', fontWeight: 'bold' }} className="px-6 py-4">Total:</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="number"
                    value={timesheetItems.reduce((sum, item) => sum + (parseFloat(item.totalHours) || 0), 0).toFixed(2)}
                    readOnly
                    className="border rounded px-2 py-1 bg-gray-100 font-semibold"
                  />
                </td>
              </tr>
            </>
          ) : (
            <tr>
              <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                No timesheet items found
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Attachments Section */}
      <div className="mt-8 border rounded-lg p-4 bg-white">
        <h3 className="text-lg font-semibold mb-4">Attachments</h3>
        
        {/* Upload Section */}
        {isEditable && (
          <div 
            className={`mb-4 p-4 border-2 border-dashed rounded-lg transition-colors duration-200 ${
              dragActive 
                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="text-center">
              
              <button
                onClick={() => document.getElementById('file-upload').click()}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors duration-200"
              >
                Upload approved timesheets (PDF, PNG, JPG, JPEG)
              </button>
              <input hidden
                type="file"
                multiple
                onChange={handleFileSelect}
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                id="file-upload"
              />
            </div>
            <br/>
            
            {uploading && (
              <div className="mt-4 text-center">
                <div className="inline-flex items-center space-x-2">
                  <img 
                    src="https://cdn-icons-png.flaticon.com/512/1792/1792671.png" 
                    alt="Loading"
                    className="animate-spin h-2.5 w-2.5 text-blue-500"
                  />
                  <span className="text-sm text-gray-600">Uploading files...</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Attachments List */}
        {attachments.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <span bold className="text-sm text-gray-600">Uploaded timesheets below :</span>
            <table className="min-w-full divide-y divide-gray-200">
              <tbody className="bg-white divide-y divide-gray-200">
                {attachments.map((attachment) => (
                  <tr 
                    key={attachment.id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleDownload(attachment.id, attachment.filename);
                        }}
                        className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
                        title="Open"
                      >
                        {attachment.filename}
                      </a>
                    </td>
                    {isEditable && (
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <img 
                          src="https://cdn-icons-png.flaticon.com/512/1828/1828843.png" 
                          alt="Delete"
                          className="w-4 h-4"
                          style={{width: '25px', height: 'auto'}}
                          onClick={() => handleDelete(attachment.id)}
                          title="Delete"
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="mt-12 flex justify-center">
        <button
          onClick={handleSubmit}
          disabled={!isEditable}
          style={{
            backgroundColor: !isEditable ? '#9CA3AF' : '#059669',
            cursor: !isEditable ? 'not-allowed' : 'pointer',
            opacity: !isEditable ? 0.7 : 1
          }}
          className="px-6 py-2.5 text-white rounded-lg transition-colors duration-200 hover:bg-green-700"
        >
          Submit
        </button>
      </div>

      {submitError && (
        <div style={{ color: 'red', marginTop: '10px', textAlign: 'center' }}>{submitError}</div>
      )}

      {message && <div className="form-message">{message}</div>}

      {showAnnotator && annotationImageUrl && (
  <div
  style={{
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    backgroundColor: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}
    onClick={() => {
      URL.revokeObjectURL(annotationImageUrl);
      setShowAnnotator(false);
      setAnnotationImageUrl(null);
    }}
  >
<div
  style={{
    width: '95vw',
    maxWidth: '1200px',
    height: '90vh',
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px',
    position: 'relative',
    boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
  }}
  onClick={(e) => e.stopPropagation()}
>

      {/* Header */}
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-semibold" style={{ color: "green" }}>
          Click on approximate center of hours worked and drag. Make sure it matches total billable hours.
        </h3>
      </div>

      {/* Annotator */}
      <div className="h-full overflow-hidden">
        
      <TimesheetAnnotator
        imageUrl={annotationImageUrl}
        originalFileName={pendingImageRef.current?.name}
        onClose={() => {
          URL.revokeObjectURL(annotationImageUrl);
          setShowAnnotator(false);
          setAnnotationImageUrl(null);
        }}
        onSave={(annotatedFile) => {
          handleUpload([annotatedFile]);

          pendingImageRef.current = null;

          URL.revokeObjectURL(annotationImageUrl);
          setShowAnnotator(false);
          setAnnotationImageUrl(null);
        }
      }
      />

      </div>
    </div>
  </div>
)}

    </div>
  );
}

export default QuickTime; 